/**
 * Main App Component
 * Entry point for the application
 */

import React, { useEffect, useRef, useState } from 'react';
import { View, Text, Platform, StyleSheet, Animated, TouchableOpacity } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { StatusBar } from 'expo-status-bar';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { NavigationContainer, LinkingOptions } from '@react-navigation/native';
import type { NavigationState } from '@react-navigation/native';
import * as Linking from 'expo-linking';
import { StripeProvider } from '@stripe/stripe-react-native';
import { useAuthStore } from './store/authStore';
import { DemoModeProvider } from './contexts/DemoModeContext';
import { ChatVisibilityProvider } from './contexts/ChatVisibilityContext';
import AppNavigator from './navigation/AppNavigator';
import { colors } from './theme/colors';
import { analytics } from './utils/analytics';
import logger from './utils/logger';
import WhatsAppFloatingButton from './components/common/WhatsAppFloatingButton';

// Stripe publishable key (test mode)
const STRIPE_PUBLISHABLE_KEY = process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY || 'pk_test_your_key_here';

// Deep linking configuration
const prefix = Linking.createURL('/');
const linking: LinkingOptions<any> = {
  prefixes: [
    prefix,
    'http://localhost:8081',
    'http://localhost:19006',
    'http://localhost:3000',
    'http://localhost:3002',
    'https://app.wealfi.co',
    'https://app-staging.wealfi.co',
    'https://app-staging.wealfi.co',
    'https://wealfi.co',
    'https://www.wealfi.co',
  ],
  config: {
    screens: {
      Onboarding: 'onboarding',
      MagicAuth: 'auth/magic-link',
      GoogleAuth: 'auth/google',
      AuthError: 'auth/error',
      Main: {
        screens: {
          MainContent: 'app',
          EditProfile: 'app/profile/edit',
          EditFinancialProfile: 'app/profile/financial',
          FinancialDiagnosticWizard: 'app/diagnostic',
          ImportBankStatementsWizard: 'app/import-pdf',
          SubscriptionSuccess: 'app/subscription/success',
          SubscriptionCancel: 'app/subscription/cancel',
          // Path without /app/ prefix so provider redirect to /bank/callback matches directly
          BankConnection: 'bank/callback',
        },
      },
      Auth: {
        screens: {
          Landing: '',
          'fr/': 'fr',
          'fr/young-professional': 'fr/young-professional',
          'fr/pages/young-professional': 'fr/pages/young-professional',
          'fr/early-saver': 'fr/early-saver',
          'fr/pages/early-saver': 'fr/pages/early-saver',
          'fr/terms': 'fr/terms',
          'fr/privacy': 'fr/privacy',
          'fr/legal': 'fr/legal',
          'fr/tools': 'fr/tools',
          'fr/tools/compound-interest-calculator': 'fr/tools/compound-interest-calculator',
          Login: 'login',
          SignUp: 'signup',
          ForgotPassword: 'forgot-password',
        },
      },
    },
  },
};

/**
 * Get the current route name from navigation state
 */
function getCurrentRouteName(state: NavigationState | undefined): string {
  if (!state || typeof state.index !== 'number') {
    return 'Unknown';
  }

  const route = state.routes[state.index];

  // If the route has nested state, recursively get the active route
  if (route.state) {
    return getCurrentRouteName(route.state as NavigationState);
  }

  return route.name;
}

export default function App() {
  const { checkAuth, isLoading, isAuthenticated, user } = useAuthStore();
  const routeNameRef = useRef<string>();
  const navigationRef = useRef();

  // Payment success overlay state
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const scaleAnim = useRef(new Animated.Value(0.8)).current;

  // Block search engine indexing on staging domains
  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const hostname = window.location.hostname;
      if (hostname.includes('staging')) {
        let tag = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
        if (!tag) {
          tag = document.createElement('meta');
          tag.setAttribute('name', 'robots');
          document.head.appendChild(tag);
        }
        tag.setAttribute('content', 'noindex, nofollow');
      }
    }
  }, []);

  useEffect(() => {
    logger.log('[App.tsx] Mounted, calling checkAuth()');

    // Initialize analytics
    analytics.initialize();

    // Check if user is already authenticated on app start
    checkAuth();

    // Handle Stripe payment redirect return (web only)
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');

      if (sessionId) {
        logger.log('[App.tsx] 🎉 Detected Stripe payment success, session_id:', sessionId);

        // Clean up URL params
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);

        // Store success flag for navigation after auth check completes
        localStorage.setItem('stripe_payment_success', 'true');
        localStorage.setItem('stripe_session_id', sessionId);
      }

      // Note: bank callback from Powens is handled directly by React Navigation
      // via the BankConnection: 'bank/callback' route in linking config
    }
  }, [checkAuth]);

  // Identify or reset user when authentication state changes
  useEffect(() => {
    if (isAuthenticated && user) {
      // User is authenticated, identify them in analytics
      analytics.identifyUser(user.id, {
        user_id: user.id,
        email: user.email || '',
        plan: 'free', // Will be updated from profile
      });
    } else if (!isAuthenticated) {
      // User logged out, reset analytics
      analytics.resetUser();
    }
  }, [isAuthenticated, user]);

  // Handle bank callback after auth is ready
  useEffect(() => {
    if (Platform.OS === 'web' && isAuthenticated && !isLoading && navigationRef.current) {
      const powensConnectionId = localStorage.getItem('bank_callback_connection_id');
      const wealfiConnectionId = localStorage.getItem('bank_callback_wealfi_connection_id');

      if (powensConnectionId) {
        logger.log('[App.tsx] 🏦 Processing bank callback, Powens ID:', powensConnectionId);

        // Clear the flag
        localStorage.removeItem('bank_callback_connection_id');
        localStorage.removeItem('bank_callback_wealfi_connection_id');

        // Navigate to BankConnection screen with the callback params
        // The screen will call the backend API to complete the connection
        (navigationRef.current as any).navigate('BankConnection', {
          externalConnectionId: powensConnectionId,
          connectionId: wealfiConnectionId,
        });
      }
    }
  }, [isAuthenticated, isLoading]);

  // Handle Stripe payment success - show overlay
  useEffect(() => {
    if (Platform.OS === 'web' && isAuthenticated && !isLoading) {
      const paymentSuccess = localStorage.getItem('stripe_payment_success');

      if (paymentSuccess === 'true') {
        logger.log('[App.tsx] 🎉 Payment success detected - showing overlay');

        // Clear the flag immediately to prevent re-triggering
        localStorage.removeItem('stripe_payment_success');
        localStorage.removeItem('stripe_session_id');

        // Show success overlay with animation
        setShowPaymentSuccess(true);

        // Start animation
        Animated.parallel([
          Animated.timing(fadeAnim, {
            toValue: 1,
            duration: 400,
            useNativeDriver: true,
          }),
          Animated.spring(scaleAnim, {
            toValue: 1,
            tension: 50,
            friction: 7,
            useNativeDriver: true,
          }),
        ]).start();
      }
    }
  }, [isAuthenticated, isLoading, fadeAnim, scaleAnim]);

  // Close payment success overlay
  const handleClosePaymentSuccess = () => {
    Animated.timing(fadeAnim, {
      toValue: 0,
      duration: 200,
      useNativeDriver: true,
    }).start(() => {
      setShowPaymentSuccess(false);
      // Reset animation values for next time
      scaleAnim.setValue(0.8);
    });
  };

  logger.log('[App.tsx] Rendering with state:', { isLoading, isAuthenticated, userId: user?.id });

  // Show loading screen while checking auth
  if (isLoading) {
    logger.log('[App.tsx] Showing loading screen');
    return (
      <SafeAreaProvider>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: colors.background }}>
          <Text style={{ color: colors.text }}>Chargement...</Text>
        </View>
      </SafeAreaProvider>
    );
  }

  logger.log('[App.tsx] Rendering AppNavigator');

  return (
    <StripeProvider
      publishableKey={STRIPE_PUBLISHABLE_KEY}
      merchantIdentifier="merchant.co.wealfi.app"
    >
      <SafeAreaProvider>
        <ChatVisibilityProvider>
        <View style={{ flex: 1 }}>
          <NavigationContainer
            ref={navigationRef}
            linking={linking}
            onReady={() => {
              // Capture initial route when navigation is ready
              const state = navigationRef.current?.getRootState();
              routeNameRef.current = getCurrentRouteName(state);
              analytics.updateGlobalProperties({
                current_page: routeNameRef.current,
              });
            }}
            onStateChange={(state) => {
              // Track navigation changes
              const previousRouteName = routeNameRef.current;
              const currentRouteName = getCurrentRouteName(state);

              if (previousRouteName !== currentRouteName) {
                // Update analytics with new screen
                analytics.updateGlobalProperties({
                  current_page: currentRouteName,
                });
              }

              // Save current route name for next comparison
              routeNameRef.current = currentRouteName;
            }}
          >
            <StatusBar style="auto" />
            <DemoModeProvider>
              <AppNavigator />
            </DemoModeProvider>
          </NavigationContainer>

          {/* WhatsApp Floating Button — hides itself when chat is visible */}
          <WhatsAppFloatingButton />

          {/* Payment Success Overlay */}
          {showPaymentSuccess && (
            <Animated.View
              style={[
                styles.successOverlay,
                { opacity: fadeAnim },
              ]}
            >
              <Animated.View
                style={[
                  styles.successContent,
                  { transform: [{ scale: scaleAnim }] },
                ]}
              >
                <View style={styles.successIconContainer}>
                  <Icon name="check" size={48} color="#FFFFFF" />
                </View>
                <Text style={styles.successTitle}>Bienvenue chez les Premium !</Text>
                <Text style={styles.successMessage}>
                  Ton abonnement est maintenant actif.{'\n'}
                  Tu as accès à toutes les fonctionnalités.
                </Text>
                <View style={styles.successFeatures}>
                  <Text style={styles.featureItem}>✨ Chat illimité avec l'IA</Text>
                  <Text style={styles.featureItem}>📊 Diagnostic financier complet</Text>
                  <Text style={styles.featureItem}>🎯 Suivi de tes objectifs</Text>
                  <Text style={styles.featureItem}>🔔 Alertes personnalisées</Text>
                </View>
                <TouchableOpacity style={styles.continueButton} onPress={handleClosePaymentSuccess}>
                  <Text style={styles.continueButtonText}>Commencer à utiliser Wealfi</Text>
                </TouchableOpacity>
              </Animated.View>
            </Animated.View>
          )}
        </View>
        </ChatVisibilityProvider>
      </SafeAreaProvider>
    </StripeProvider>
  );
}

const styles = StyleSheet.create({
  successOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#111827', // Solid dark background
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
  },
  successContent: {
    alignItems: 'center',
    padding: 32,
    maxWidth: 360,
  },
  successIconContainer: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: '#10B981',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
    shadowColor: '#10B981',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.4,
    shadowRadius: 16,
    elevation: 10,
  },
  successTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: 12,
    textAlign: 'center',
  },
  successMessage: {
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.8)',
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 24,
  },
  successFeatures: {
    alignSelf: 'stretch',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 16,
    padding: 20,
    marginBottom: 32,
  },
  featureItem: {
    fontSize: 16,
    color: '#FFFFFF',
    paddingVertical: 8,
  },
  continueButton: {
    backgroundColor: '#10B981',
    paddingHorizontal: 32,
    paddingVertical: 16,
    borderRadius: 12,
    shadowColor: '#10B981',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 6,
  },
  continueButtonText: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '600',
  },
});

